import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLang } from "@/i18n";
import api, { resolveMediaUrl } from "@/lib/api";
import SEO from "@/components/SEO";
import { ArrowLeft } from "lucide-react";

export default function Blog() {
  const { t, tt } = useLang();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/posts", { params: { published: true } }).then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <SEO title={t("nav.blog")} />
      <div className="section-warm py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.35em] text-brand-orange font-semibold mb-3">{t("nav.blog")}</div>
          <h1 className="font-serif text-5xl md:text-6xl text-brand-navy" data-testid="blog-title">{t("sections.blog_title")}</h1>
          <p className="mt-3 text-slate-700 max-w-2xl">{t("sections.blog_desc")}</p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="blog-list">
          {posts.map((p) => (
            <Link
              key={p.id}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="group bg-white rounded-2xl overflow-hidden border border-brand-gold/20 shadow-sm hover:shadow-xl transition"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img src={resolveMediaUrl(p.cover_image)} alt={tt(p.title)} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
              </div>
              <div className="p-6">
                <div className="flex flex-wrap gap-1 mb-3">
                  {p.tags?.slice(0, 3).map((t) => (
                    <span key={t} className="text-[10px] uppercase tracking-widest text-brand-orange font-semibold">#{t}</span>
                  ))}
                </div>
                <h3 className="font-serif text-2xl text-brand-navy leading-tight">{tt(p.title)}</h3>
                <p className="mt-3 text-sm text-slate-600 line-clamp-3">{tt(p.excerpt)}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const { t, tt } = useLang();
  const [post, setPost] = useState(null);

  useEffect(() => {
    api.get(`/posts/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(false));
  }, [slug]);

  if (post === false) return <div className="max-w-3xl mx-auto px-6 py-24 text-slate-500">Not found</div>;
  if (!post) return <div className="max-w-3xl mx-auto px-6 py-24">Loading…</div>;

  const body = tt(post.body);

  return (
    <div>
      <SEO title={tt(post.title)} description={tt(post.excerpt)} image={resolveMediaUrl(post.cover_image)} />
      <div className="aspect-[16/8] max-h-[500px] overflow-hidden">
        <img src={resolveMediaUrl(post.cover_image)} alt="" className="w-full h-full object-cover" />
      </div>
      <article className="max-w-3xl mx-auto px-6 md:px-10 py-16">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-brand-orange hover:text-brand-orange-dark mb-6">
          <ArrowLeft size={14}/> {t("cta.all")}
        </Link>
        <div className="flex flex-wrap gap-2 mb-4">
          {post.tags?.map((t) => (
            <span key={t} className="text-[10px] uppercase tracking-widest text-brand-orange font-semibold">#{t}</span>
          ))}
        </div>
        <h1 className="font-serif text-4xl md:text-5xl text-brand-navy leading-tight" data-testid="post-title">{tt(post.title)}</h1>
        <p className="mt-4 text-lg text-slate-700 italic">{tt(post.excerpt)}</p>
        <div className="mt-8 text-slate-800 leading-relaxed space-y-4 whitespace-pre-line">
          {body.split(/\n\n+/).map((para, i) => (
            <p key={i} dangerouslySetInnerHTML={{ __html: para.replace(/\*\*(.+?)\*\*/g, '<strong class="text-brand-navy">$1</strong>') }} />
          ))}
        </div>
      </article>
    </div>
  );
}
